import React from "react";
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Typography,
} from "@mui/material";
import { useParticipants } from "../hooks";
import { Button } from "@dippi/ui";
import { Controller, useFormContext } from "react-hook-form";
import { Brand, Product } from "@/types/entities";
import { Organization } from "@/types/utils";

interface Props {
    organizations: Organization[] | undefined;
    brands: Brand[] | undefined;
    products: Product[] | undefined;
}

const ParticipantsStepper: React.FC<Props> = ({ organizations, brands, products }) => {
    const { control, watch } = useFormContext();
    const { capitalizeMenuItems, activeStep, handleNext, handleBack, handleReset, steps } =
        useParticipants();

    return (
        <Box width={400}>
            <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((step, index) => {
                    const capLabel = capitalizeMenuItems(step.label.replace("Select ", ""));

                    const isNextDisabled =
                        (activeStep === 0 && !watch("selectedOrganizationId")) ||
                        (activeStep === 1 && !watch("selectedBrandId")) ||
                        (activeStep === 2 &&
                            (!watch("selectedProductIds") ||
                                watch("selectedProductIds").length === 0));

                    return (
                        <Step key={step.label}>
                            <StepLabel>{step.label}</StepLabel>
                            <StepContent>
                                <Box sx={{ mb: 2, pt: 1 }}>
                                    <FormControl fullWidth>
                                        <InputLabel>{capLabel}</InputLabel>

                                        {step.step === "organization" && (
                                            <Controller
                                                name="selectedOrganizationId"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select {...field} label={capLabel}>
                                                        {organizations?.map(org => (
                                                            <MenuItem key={org._id} value={org._id}>
                                                                {org.title}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                        )}

                                        {step.step === "brand" && (
                                            <Controller
                                                name="selectedBrandId"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select {...field} label={capLabel}>
                                                        {brands?.map(brand => (
                                                            <MenuItem
                                                                key={brand._id}
                                                                value={brand._id}
                                                            >
                                                                {brand.title}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                        )}

                                        {step.step === "product" && (
                                            <Controller
                                                name="selectedProductIds"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        multiple
                                                        label={capLabel}
                                                        onChange={event => {
                                                            const selectedProducts =
                                                                event.target.value;
                                                            field.onChange(selectedProducts);
                                                        }}
                                                    >
                                                        {products?.map(product => (
                                                            <MenuItem
                                                                key={product._id}
                                                                value={product._id}
                                                            >
                                                                {product.longTitle}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                        )}
                                    </FormControl>
                                </Box>

                                <Box marginBottom={2} display="flex" gap={1}>
                                    <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        disabled={isNextDisabled}
                                    >
                                        {index === steps.length - 1 ? "Finish" : "Continue"}
                                    </Button>
                                    <Button disabled={index === 0} onClick={handleBack}>
                                        Back
                                    </Button>
                                </Box>
                            </StepContent>
                        </Step>
                    );
                })}
            </Stepper>
            {activeStep === steps.length && (
                <Paper square elevation={0} sx={{ p: 3 }}>
                    <Typography>Participants are added</Typography>
                    <Button variant="outlined" onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
                        Add more
                    </Button>
                </Paper>
            )}
        </Box>
    );
};

export default ParticipantsStepper;
